import {Children, memo, ReactElement} from 'react';
import {MetaTag} from './meta-tag';
import {TitleMetaTagChildren} from './static-page-title';
import {shallowEqual} from '@app/ui/utils/shallow-equal';
import {isSsr} from '@app/ui/utils/is-ssr';

const rafPolyfill = (() => {
  let clock = Date.now();
  return (callback: Function) => {
    const currentTime = Date.now();
    if (currentTime - clock > 16) {
      clock = currentTime;
      callback(currentTime);
    } else {
      setTimeout(() => {
        rafPolyfill(callback);
      }, 0);
    }
  };
})();

const cafPolyfill = (id: string | number) => clearTimeout(id);

const requestAnimationFrame = !isSsr()
  ? window.requestAnimationFrame
  : global.requestAnimationFrame || rafPolyfill;

const cancelAnimationFrame = !isSsr()
  ? window.cancelAnimationFrame
  : global.cancelAnimationFrame || cafPolyfill;

export const helmetAttribute = 'data-be-helmet';
let rafId: number | null;

interface HelmetProps {
  children?: ReactElement | ReactElement[];
  tags?: MetaTag[];
}

export const Helmet = memo(({children, tags}: HelmetProps) => {
  if (isSsr()) return null;

  if (!tags && children) {
    tags = mapChildrenToTags(children);
  }

  updateTags(tags);
  return null;
}, shallowEqual);

function mapChildrenToTags(
  children: ReactElement | ReactElement[]
): MetaTag[] {
  return Children.map(children, child => {
    const element = child as ReactElement<any>; // 👈 Cast to known ReactElement

    switch (element.type) {
      case 'title':
        return {
          nodeName: 'title',
          _text: titleTagChildrenToString(element.props.children),
        };
      case 'meta':
        return {...element.props, nodeName: 'meta'};
      default:
        return undefined; // or ignore
    }
  })?.filter(Boolean) as MetaTag[];
}

function titleTagChildrenToString(children: TitleMetaTagChildren): string {
  if (children == null) return '';
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.map(c => titleTagChildrenToString(c)).join('');
  }

  // If children is a React element with .props.children
  if ('props' in children) {
    return titleTagChildrenToString(children.props.children);
  }

  // If children has "message" or i18n structure, just fallback to raw string
  if ('message' in children) {
    return children.message;
  }

  return String(children);
}

function removeOldTags() {
  if (isSsr()) return;
  document.head
    .querySelectorAll('meta:not([data-keep]), title, link[rel="canonical"]')
    .forEach(tag => {
      document.head.removeChild(tag);
    });
}

function updateTags(tags?: MetaTag[] | string) {
  if (rafId) cancelAnimationFrame(rafId);

  rafId = requestAnimationFrame(() => {
    removeOldTags();

    if (typeof tags === 'string') {
      const template = document.createElement('template');
      template.innerHTML = tags;
      template.content.childNodes.forEach(node => {
        if (node instanceof HTMLElement) {
          node.setAttribute(helmetAttribute, 'true');
          document.head.prepend(node);
        }
      });
    } else {
      tags?.forEach(updateTag);
    }

    rafId = null;
  });
}

function updateTag(tag: MetaTag) {
  if (tag.nodeName === 'title') {
    if (typeof tag._text !== 'undefined' && document.title !== tag._text) {
      document.title = tag._text;
    }
    return;
  }

  const newElement = document.createElement(tag.nodeName);
  for (const key in tag) {
    const attribute = key as keyof MetaTag;
    if (attribute === 'nodeName') continue;

    if (attribute === '_text') {
      newElement.textContent =
        typeof tag._text === 'string' ? tag._text : JSON.stringify(tag._text);
    } else {
      const value = tag[attribute] == null ? '' : tag[attribute];
      newElement.setAttribute(attribute, value as string);
    }
  }

  newElement.setAttribute(helmetAttribute, 'true');
  document.head.prepend(newElement);
}
